/**
 * Model Audit Mode — admin queue page at /model/audit.
 *
 * Pick an ops day, see every auditable delivery from it (completed that day
 * with at least one extraction run), and walk them one-by-one inside the
 * real QA detail page in audit mode (`?audit=1&date=...`). Grading writes
 * ONLY model_audit_findings — QA decisions, agent labels, and training
 * queues are untouched.
 *
 * Mirrors the annotation-review queue page: the date lives in the URL so
 * refresh/back/deep-links work, and the list-shaping happens server-side
 * (GET /api/admin/model-audit/queue).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import {
  AUDIT_FIELDS,
  AUDIT_SIDES,
  AUDIT_FIELD_LABELS,
  type AuditVerdicts,
} from "@shared/model-audit";
import { auditDeliveryPath } from "@/lib/model-audit";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardCheck, Play, AlertCircle } from "lucide-react";

/** The day this workflow was built for — Sarah's Sunday Aug 16 sweep. */
const DEFAULT_DATE = "2026-08-16";

interface QueueRow {
  id: string;
  shortId: string | null;
  address: string | null;
  workerName: string | null;
  completedAt: string | null;
  expectedStreetNumber: string | null;
  expectedUnitNumber: string | null;
  expectedDzeCode: string | null;
  actualStreetNumber: string | null;
  actualUnitNumber: string | null;
  actualDzeCode: string | null;
  verdicts: AuditVerdicts | null;
  note: string | null;
  graded: boolean;
  errorCount: number | null;
}

interface QueueResponse {
  date: string;
  total: number;
  graded: number;
  rows: QueueRow[];
}

interface SummaryResponse {
  date: string;
  graded: number;
  tallies: Record<string, Record<string, Record<string, number>>>;
  /** bySource[side][field][sourceLabel][verdict] — provenance split. */
  bySource: Record<string, Record<string, Record<string, Record<string, number>>>>;
}

/** "PDR 97% (117) · notes kw 92% (26)" — accuracy per source over gradeable
 * cells (correct+wrong+missed; N/A excluded), biggest bucket first. */
function sourceBreakdownLine(
  bySource: SummaryResponse["bySource"],
  side: string,
  field: string,
): string {
  const buckets = bySource?.[side]?.[field] ?? {};
  const parts = Object.entries(buckets)
    .map(([source, t]) => {
      const gradeable = (t.correct ?? 0) + (t.wrong ?? 0) + (t.missed ?? 0);
      return { source, gradeable, pct: gradeable > 0 ? Math.round(((t.correct ?? 0) / gradeable) * 100) : null };
    })
    .filter((b) => b.gradeable > 0)
    .sort((a, b) => b.gradeable - a.gradeable);
  return parts.map((b) => `${b.source} ${b.pct}% (${b.gradeable})`).join(" · ");
}

export default function ModelAudit() {
  const search = useSearch();
  const [, setLocation] = useLocation();

  const date = useMemo(() => {
    const p = new URLSearchParams(search);
    const d = p.get("date") ?? "";
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : DEFAULT_DATE;
  }, [search]);

  const setDate = (d: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
    setLocation(`/model/audit?date=${d}`);
  };

  const queueQuery = useQuery<QueueResponse>({
    queryKey: ["/api/admin/model-audit/queue", date],
    queryFn: async () => {
      const res = await fetch(`/api/admin/model-audit/queue?date=${date}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const summaryQuery = useQuery<SummaryResponse>({
    queryKey: ["/api/admin/model-audit/summary", date],
    queryFn: async () => {
      const res = await fetch(`/api/admin/model-audit/summary?date=${date}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const queue = queueQuery.data;
  const firstUngraded = queue?.rows.find((r) => !r.graded);
  const startTarget = firstUngraded ?? queue?.rows[0];

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="page-model-audit">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Model Audit
              </CardTitle>
              <CardDescription>
                Walk a day's deliveries in the QA detail page and grade the model's
                Expected/Actual predictions (street #, unit #, drop zone). Findings are
                stored separately — nothing here touches QA decisions.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-40"
                data-testid="input-audit-date"
              />
              <Button
                disabled={!startTarget}
                onClick={() => startTarget && setLocation(auditDeliveryPath(startTarget.id, date))}
                data-testid="button-start-audit"
              >
                <Play className="h-4 w-4 mr-1" />
                {firstUngraded && queue && queue.graded > 0 ? "Resume audit" : "Start audit"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {queueQuery.isLoading ? (
            <Skeleton className="h-6 w-64" />
          ) : queueQuery.isError ? (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" /> Failed to load the audit queue.
            </div>
          ) : queue ? (
            <div className="text-sm text-muted-foreground" data-testid="text-audit-progress-summary">
              <span className="font-semibold text-foreground">{queue.graded}</span> of{" "}
              <span className="font-semibold text-foreground">{queue.total}</span> deliveries graded
              for {queue.date}.
            </div>
          ) : null}

          {/* Verdict tallies — only once something is graded */}
          {summaryQuery.data && summaryQuery.data.graded > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="audit-summary-tiles">
              {AUDIT_FIELDS.map((field) => (
                <div key={field} className="rounded-md border p-3">
                  <div className="text-xs font-bold mb-2">{AUDIT_FIELD_LABELS[field]}</div>
                  {AUDIT_SIDES.map((side) => {
                    const t = summaryQuery.data!.tallies[side]?.[field] ?? {};
                    const correct = t.correct ?? 0;
                    const wrong = t.wrong ?? 0;
                    const missed = t.missed ?? 0;
                    const na = t.not_present ?? 0;
                    const gradeable = correct + wrong + missed;
                    const pct = gradeable > 0 ? Math.round((correct / gradeable) * 100) : null;
                    const breakdown = sourceBreakdownLine(summaryQuery.data!.bySource, side, field);
                    return (
                      <div key={side} className="py-0.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground capitalize">{side}</span>
                          <span>
                            {pct !== null ? <span className="font-semibold">{pct}%</span> : <span className="text-muted-foreground">—</span>}
                            <span className="text-muted-foreground ml-2">
                              ✓{correct} ✗{wrong} missed {missed} n/a {na}
                            </span>
                          </span>
                        </div>
                        {breakdown && (
                          <div className="text-[10px] text-muted-foreground text-right" data-testid={`source-breakdown-${side}-${field}`}>
                            {breakdown}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          {queueQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : queue && queue.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No auditable deliveries for {date} (completed that day with at least one
              extraction run).
            </div>
          ) : queue ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Exp (St / Unit / DZ)</TableHead>
                    <TableHead>Act (St / Unit / DZ)</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.rows.map((row, i) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => setLocation(auditDeliveryPath(row.id, date))}
                      data-testid={`audit-row-${row.id}`}
                    >
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-sm max-w-[280px] truncate">{row.address ?? "—"}</TableCell>
                      <TableCell className="text-sm">{row.workerName ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.expectedStreetNumber ?? "—"} / {row.expectedUnitNumber ?? "—"} / {row.expectedDzeCode ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.actualStreetNumber ?? "—"} / {row.actualUnitNumber ?? "—"} / {row.actualDzeCode ?? "—"}
                      </TableCell>
                      <TableCell>
                        {!row.graded ? (
                          <Badge variant="outline">Not graded</Badge>
                        ) : row.errorCount && row.errorCount > 0 ? (
                          <Badge variant="destructive">{row.errorCount} model error{row.errorCount > 1 ? "s" : ""}</Badge>
                        ) : (
                          <Badge variant="secondary">Clean</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
